import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { Visibility } from "@storyai/shared";
import { ApiError } from "../lib/api";

// ---- PageHeader -------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
  crumbs,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  crumbs?: CrumbPart[];
}) {
  return (
    <header className="page-header">
      {crumbs && <Crumbs parts={crumbs} />}
      <div className="page-header-row">
        <div>
          <h1>{title}</h1>
          {subtitle && <p className="muted page-subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
    </header>
  );
}

// ---- Crumbs -----------------------------------------------------------------

export interface CrumbPart {
  to?: string;
  label: string;
}

export function Crumbs({ parts }: { parts: CrumbPart[] }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && <span className="muted crumb-sep">/</span>}
          {p.to ? <Link to={p.to}>{p.label}</Link> : <span>{p.label}</span>}
        </span>
      ))}
    </nav>
  );
}

// ---- Field ------------------------------------------------------------------

export function Field({
  label,
  hint,
  error,
  children,
  inline,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  /** Checkbox-style: control before label, on one line. */
  inline?: boolean;
}) {
  if (inline) {
    return (
      <label className="field field-inline">
        {children}
        <span>
          <span className="field-label">{label}</span>
          {hint && <span className="muted small field-hint"> {hint}</span>}
        </span>
        {error && <span className="field-error">{error}</span>}
      </label>
    );
  }
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="muted small field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

// ---- InlineConfirm ----------------------------------------------------------

export function InlineConfirm({
  label,
  question = "Are you sure?",
  confirmLabel = "Yes",
  onConfirm,
  busy,
  danger = true,
  className,
  defaultOpen = false,
  onCancel,
}: {
  label: ReactNode;
  question?: ReactNode;
  confirmLabel?: ReactNode;
  onConfirm: () => void;
  busy?: boolean;
  danger?: boolean;
  className?: string;
  /** Start in the "Are you sure?" state (e.g. after a Delete keypress). */
  defaultOpen?: boolean;
  /** Called when the user backs out of the confirmation. */
  onCancel?: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (!open) {
    return (
      <button
        type="button"
        className={`btn btn-sm ${danger ? "btn-danger-ghost" : ""} ${className ?? ""}`}
        onClick={() => setOpen(true)}
        disabled={busy}
      >
        {label}
      </button>
    );
  }
  return (
    <span className="inline-confirm">
      <span className="small">{question}</span>
      <button
        type="button"
        className={`btn btn-sm ${danger ? "btn-danger" : "btn-primary"}`}
        onClick={() => {
          onConfirm();
          setOpen(false);
        }}
        disabled={busy}
      >
        {confirmLabel}
      </button>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => {
          setOpen(false);
          onCancel?.();
        }}
        disabled={busy}
      >
        No
      </button>
    </span>
  );
}

// ---- VisibilityBadge --------------------------------------------------------

const VISIBILITY_LABEL: Record<Visibility, string> = {
  private: "Private",
  unlisted: "Unlisted",
  public: "Public",
};

export function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  return <span className={`badge badge-${visibility}`}>{VISIBILITY_LABEL[visibility]}</span>;
}

export function Badge({ children, tone }: { children: ReactNode; tone?: "accent" | "muted" }) {
  return <span className={`badge ${tone ? `badge-${tone}` : ""}`}>{children}</span>;
}

// ---- EmptyState -------------------------------------------------------------

export function EmptyState({
  title,
  children,
  action,
}: {
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-title">{title}</div>
      {children && <div className="muted empty-body">{children}</div>}
      {action && <div className="empty-action">{action}</div>}
    </div>
  );
}

// ---- Errors -----------------------------------------------------------------

/** Full-panel error for a failed page query. */
export function ErrorPanel({ error }: { error: unknown }) {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return (
        <EmptyState title="Not found">
          This doesn't exist, or you don't have access to it.{" "}
          <Link to="/stories">Back to Public Stories</Link>
        </EmptyState>
      );
    }
    if (error.status === 403) {
      return (
        <EmptyState title="You don't have access">
          Only the author can view this page. <Link to="/stories">Back to Public Stories</Link>
        </EmptyState>
      );
    }
    return <EmptyState title="Something went wrong">{error.message}</EmptyState>;
  }
  return <EmptyState title="Something went wrong">Please try again.</EmptyState>;
}

/** Inline error for a failed mutation, including 400 issues. */
export function MutationError({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : "Something went wrong";
  const issues = error instanceof ApiError ? error.issues : undefined;
  return (
    <div className="error" role="alert">
      <div>{msg}</div>
      {issues && issues.length > 0 && (
        <ul className="issues">
          {issues.map((i, n) => (
            <li key={n}>
              {i.path && <code>{i.path}</code>} {i.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Brief "Saved" flash that clears itself. */
export function SavedFlash({ token }: { token: number }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (token === 0) return;
    setShow(true);
    const t = setTimeout(() => setShow(false), 2000);
    return () => clearTimeout(t);
  }, [token]);
  if (!show) return null;
  return <span className="status-ok small">Saved</span>;
}

export function Loading() {
  return <p className="muted">Loading…</p>;
}

export function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
